// Pestaña Pagos del detalle de Cobranza: el libro de pagos del crédito (motor y tienda), del más
// reciente al más viejo, con dónde, cómo y quién recibió cada pago.
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import type { LoanPaymentListItem } from '@/api/services/loan-credit';
import { CreditBlock, CreditChip, CreditEmptyNote } from '../../creditFile/components/CreditFileUi';
import type { CreditPaymentsResult } from '../api/useCreditPayments';
import {
    appliedTotalOf,
    collectionResultPresentationOf,
    formatPaymentCents,
    paymentCouponsOf,
} from '../payments';
import { paymentChargeKindLabelOf, paymentOriginFactsOf } from '../../payments/paymentOrigin';

const CELL =
    'overflow-hidden text-ellipsis whitespace-nowrap border-b border-border/60 px-1 py-[3px] align-middle text-[10.5px] tabular-nums';

/** Folio, Tipo y Cómo solo con el panel ancho; en uno angosto bajan como sub-línea. */
const WIDE_ONLY = 'hidden @lg:table-cell';

/** La sub-línea que reemplaza a una columna oculta en el panel angosto. */
const NARROW_SUBLINE = 'block truncate text-[9px] leading-tight text-muted-foreground @lg:hidden';

/** Las columnas del historial; las que no llevan ancho se reparten lo que sobra. */
const COLUMNS: readonly (readonly [
    label: string,
    numeric: boolean,
    width?: string,
    visibility?: string,
])[] = [
    ['Fecha y hora', false, 'w-[72px] @lg:w-[84px]'],
    ['Folio', false, undefined, WIDE_ONLY],
    ['Monto', true, 'w-[76px] @lg:w-[88px]'],
    ['Tipo', false, 'w-[64px]', WIDE_ONLY],
    ['Dónde', false],
    ['Cómo', false, undefined, WIDE_ONLY],
    ['Quién lo recibió', false],
    ['Resultado', false, 'w-[72px] @lg:w-[96px]'],
    ['Cuotas cubiertas', false, 'w-[60px] @lg:w-[96px]'],
];

const NO_VALUE = '—';

/** «Historial · 8 pagos · $5,494.70 total»; con páginas pendientes lo dice. */
export function paymentsTitleOf(rows: readonly LoanPaymentListItem[], hasMore: boolean): string {
    const count = rows.length === 1 ? '1 pago' : `${String(rows.length)} pagos`;
    const title = `Historial · ${count} · ${appliedTotalOf(rows)} total`;
    return hasMore ? `${title} · hay más por cargar` : title;
}

/** Sub-líneas de Monto: lo cobrado si difiere de lo aplicado y el excedente si lo hubo. */
function amountBreakdownOf(payment: LoanPaymentListItem): {
    applied: string;
    extras: string[];
    title: string;
} {
    const money = (cents: number) => formatPaymentCents(cents, payment.currency);
    const applied = money(payment.appliedCents);
    const extras: string[] = [];
    if (payment.collectedCents !== payment.appliedCents) {
        extras.push(`Cobrado ${money(payment.collectedCents)}`);
    }
    if (payment.overpaidCents > 0) extras.push(`Excedente ${money(payment.overpaidCents)}`);
    return {
        applied,
        extras,
        title: [
            `Aplicado ${applied}`,
            `Cobrado ${money(payment.collectedCents)}`,
            `Excedente ${money(payment.overpaidCents)}`,
        ].join(' · '),
    };
}

/** «Cupón #4», «Cupones #6 a #9»; sin cupones que nombrar, «—» (el tipo ya sale en «Tipo»). */
function coveredInstallmentOf(payment: LoanPaymentListItem): string {
    return paymentCouponsOf(payment) ?? NO_VALUE;
}

export function PaymentRow({ payment }: { payment: LoanPaymentListItem }) {
    const result = collectionResultPresentationOf(payment.result);
    const origin = paymentOriginFactsOf(payment);
    const kind = paymentChargeKindLabelOf(payment.chargeKind);
    const who = origin.who ?? NO_VALUE;
    const amount = amountBreakdownOf(payment);
    return (
        <tr className={payment.needsReview ? 'bg-warning-soft/40' : undefined}>
            <td
                className={cn(CELL, 'text-[10px]')}
                title={`${formatDateTimeMx(payment.appliedAt)} · ${payment.receipt}`}
            >
                <span className="block truncate" title={formatDateTimeMx(payment.appliedAt)}>
                    {formatDateTimeMx(payment.appliedAt)}
                </span>
                <span
                    className="block truncate font-mono text-[9px] leading-tight text-muted-foreground @lg:hidden"
                    title={payment.receipt}
                >
                    {payment.receipt}
                </span>
            </td>
            <td
                className={cn(CELL, WIDE_ONLY, 'font-mono text-[10px] text-muted-foreground')}
                title={payment.receipt}
            >
                {payment.receipt}
            </td>
            <td
                className="whitespace-nowrap border-b border-border/60 px-1 py-[3px] text-right align-middle text-[10.5px] tabular-nums"
                title={`${kind} · ${amount.title}`}
            >
                <span className="block font-bold">{amount.applied}</span>
                <span className={NARROW_SUBLINE} title={kind}>
                    {kind}
                </span>
                {amount.extras.map((extra) => (
                    <span
                        key={extra}
                        className="block whitespace-normal text-[9px] leading-tight text-muted-foreground"
                    >
                        {extra}
                    </span>
                ))}
            </td>
            <td className={cn(CELL, WIDE_ONLY)} title={kind}>
                {kind}
            </td>
            <td className={CELL} title={`${origin.where} · ${origin.how}`}>
                <span className="block truncate" title={origin.where}>
                    {origin.where}
                </span>
                <span className={NARROW_SUBLINE} title={origin.how}>
                    {origin.how}
                </span>
            </td>
            <td className={cn(CELL, WIDE_ONLY)} title={origin.how}>
                {origin.how}
            </td>
            <td className={CELL} title={who}>
                {who}
            </td>
            <td className={cn(CELL, 'overflow-visible whitespace-normal')}>
                <span className="flex flex-col items-start gap-0.5">
                    <CreditChip tone={result.tone}>{result.label}</CreditChip>
                    {payment.needsReview ? (
                        <CreditChip tone="warning" className="leading-tight">
                            Requiere revisión
                        </CreditChip>
                    ) : null}
                </span>
            </td>
            <td className={cn(CELL, 'whitespace-normal')}>
                <span className="block leading-tight">{coveredInstallmentOf(payment)}</span>
                {payment.creditPrincipalBalanceAfterCents === undefined ? null : (
                    <span className="block text-[9px] leading-tight text-muted-foreground">
                        Saldo capital{' '}
                        {formatPaymentCents(
                            payment.creditPrincipalBalanceAfterCents,
                            payment.currency,
                        )}
                    </span>
                )}
            </td>
        </tr>
    );
}

export interface InstallmentPaymentsTabProps {
    payments: CreditPaymentsResult;
    /** En una tarjeta que ya titula (el expediente), el título propio sobra. */
    showHeading?: boolean;
}

export function InstallmentPaymentsTab({
    payments,
    showHeading = true,
}: InstallmentPaymentsTabProps) {
    const rows = payments.payments;
    const hasRows = rows.length > 0;

    return (
        <CreditBlock
            isLoading={payments.isLoading}
            failure={payments.failure}
            subject="los pagos"
            onRetry={payments.retry}
        >
            {showHeading ? (
                <h3 className="mb-2 text-[11.5px] font-semibold">
                    {hasRows ? paymentsTitleOf(rows, payments.hasNextPage) : 'Historial de pagos'}
                </h3>
            ) : null}
            {hasRows ? (
                <>
                    <div className="@container">
                        <table className="w-full table-fixed border-collapse">
                            <thead>
                                <tr>
                                    {COLUMNS.map(([label, numeric, width, visibility]) => (
                                        <th
                                            key={label}
                                            scope="col"
                                            className={cn(
                                                'border-b border-border bg-muted/40 px-1 py-[3px] align-bottom text-[9px] font-semibold uppercase leading-tight tracking-[0.45px] text-muted-foreground',
                                                numeric ? 'text-right' : 'text-left',
                                                width,
                                                visibility,
                                            )}
                                        >
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((payment) => (
                                    <PaymentRow key={payment.paymentId} payment={payment} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {payments.nextPageFailure === null ? null : (
                        <p role="alert" className="pt-2 text-[10.5px] text-danger">
                            {payments.nextPageFailure}
                        </p>
                    )}
                    {payments.hasNextPage ? (
                        <div className="flex justify-center p-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                isLoading={payments.isFetchingNextPage}
                                onClick={payments.fetchNextPage}
                            >
                                Cargar más
                            </Button>
                        </div>
                    ) : null}
                </>
            ) : (
                <CreditEmptyNote>Este crédito todavía no tiene pagos aplicados.</CreditEmptyNote>
            )}
        </CreditBlock>
    );
}
